/**
 * Storage utility functions for both browser (cookies) and React Native (AsyncStorage)
 * Automatically detects the environment and uses the appropriate storage method
 */

import { getDayKeyBefore } from './countdown';

// Try to import AsyncStorage for React Native (will be null in browser)
// Use a lazy getter to prevent webpack from statically analyzing the require
let AsyncStorage = null;
const getAsyncStorage = () => {
  // React Native: use global.AsyncStorage if the app set it (e.g. mobile App.js)
  // This ensures guesses persist when opening settings or when window/document are polyfilled
  if (typeof global !== 'undefined' && global.AsyncStorage) {
    return global.AsyncStorage;
  }
  if (AsyncStorage !== null) {
    return AsyncStorage;
  }
  
  // Only try to require AsyncStorage if we're NOT in a browser environment
  // Check for browser globals first to prevent webpack from analyzing the require
  if (typeof window !== 'undefined' || typeof document !== 'undefined') {
    return null;
  }
  
  try {
    if (typeof require !== 'undefined') {
      // Use Function constructor to prevent webpack from statically analyzing this require
      // This is safe because we've already checked we're not in a browser
      const requireFn = new Function('return require')();
      // Split the module name to prevent static analysis
      const modulePath = ['@react-native-async-storage', 'async-storage'].join('/');
      const asyncStorageModule = requireFn(modulePath);
      // Handle both default export and named export
      AsyncStorage = asyncStorageModule.default || asyncStorageModule;
      return AsyncStorage;
    }
  } catch (e) {
    // AsyncStorage not available
    AsyncStorage = null;
  }
  
  return null;
};

/**
 * Consent key used by the web client to store cookie/storage preference.
 * Only used in browser; when value is 'none', we do not write cookies or localStorage.
 */
const COOKIE_CONSENT_KEY = 'logiword_cookie_consent';

/**
 * In the browser, check if the user has accepted functional storage (cookies/localStorage).
 * When false, all writes are skipped. React Native is not gated.
 * @returns {boolean}
 */
function isStorageConsentGranted() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return true; // Non-browser (e.g. React Native): allow
  }
  try {
    return localStorage.getItem(COOKIE_CONSENT_KEY) === 'functional';
  } catch (e) {
    return false;
  }
}

/**
 * Check if we're in a React Native environment
 * @returns {boolean}
 */
function isReactNative() {
  // Check global.AsyncStorage first so mobile app's setup is used
  if (typeof global !== 'undefined' && global.AsyncStorage) {
    return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
  }
  const storage = getAsyncStorage();
  return (
    typeof navigator !== 'undefined' &&
    navigator.product === 'ReactNative' &&
    storage !== null
  );
}

/**
 * Set a cookie with a value (browser only)
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration (default: 1)
 */
function setCookie(name, value, days = 1) {
  if (typeof document === 'undefined') {
    return;
  }

  const encodedValue = encodeURIComponent(value);
  const cookieSize = name.length + encodedValue.length + 50; // Approximate size including metadata
  
  if (cookieSize > 4096) {
    return;
  }

  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const cookieString = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/`;
  document.cookie = cookieString;
  
  // Cookie set (verification omitted to avoid console output)
}

/**
 * Get a cookie value by name (browser only)
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
function getCookie(name) {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEQ = name + '=';
  const allCookies = document.cookie;
  const ca = allCookies.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const value = decodeURIComponent(c.substring(nameEQ.length, c.length));
      return value;
    }
  }
  
  return null;
}

/**
 * Delete a cookie by name (browser only)
 * @param {string} name - Cookie name
 */
function deleteCookie(name) {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

/**
 * Save guesses to a day-specific and language-specific storage (cookie for web, AsyncStorage for React Native)
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {Array} guesses - Array of guess objects { word, score, percentile }
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 */
export async function saveGuessesToCookie(dayKey, guesses, lang = 'en') {
  const storageKey = `semantle-guesses-${lang}-${dayKey}`;
  try {
    // Never save empty guesses - this should be checked before calling, but double-check here
    if (!guesses || guesses.length === 0) {
      return;
    }
    
    // Check if cookie already exists and has data - don't overwrite with empty
    if (typeof document !== 'undefined') {
      const existing = getCookie(storageKey);
      if (existing) {
        try {
          const existingGuesses = JSON.parse(existing);
          if (Array.isArray(existingGuesses) && existingGuesses.length > 0 && guesses.length === 0) {
            return;
          }
        } catch (e) {
          // Existing cookie is corrupted, proceed with save
        }
      }
    }
    
    const data = JSON.stringify(guesses);
    const dataSize = new Blob([data]).size;
    
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      // Use AsyncStorage for React Native
      await storage.setItem(storageKey, data);
    } else if (typeof document !== 'undefined') {
      if (!isStorageConsentGranted()) return;
      // Use cookies for browser (with localStorage fallback)
      // Store for 2 days to handle edge cases around midnight
      setCookie(storageKey, data, 2);
      
      // Also save to localStorage as backup (cookies can be unreliable)
      try {
        localStorage.setItem(storageKey, data);
      } catch (e) {}
      
      // Cookie set (verification omitted to avoid console output)
    }
  } catch (err) {}
}

/**
 * Load guesses from a day-specific and language-specific storage (cookie for web, AsyncStorage for React Native)
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 * @returns {Promise<Array|null>} Array of guess objects or null if not found/invalid
 */
export async function loadGuessesFromCookie(dayKey, lang = 'en') {
  const storageKey = `semantle-guesses-${lang}-${dayKey}`;
  try {
    let storedValue = null;
    
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      // Use AsyncStorage for React Native
      storedValue = await storage.getItem(storageKey);
    } else if (typeof document !== 'undefined') {
      // Use cookies for browser (with localStorage fallback)
      storedValue = getCookie(storageKey);
      
      // If cookie not found, try localStorage as fallback
      if (!storedValue) {
        try {
          const fromLocalStorage = localStorage.getItem(storageKey);
          if (fromLocalStorage) {
            storedValue = fromLocalStorage;
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    }
    
    if (!storedValue) {
      return null;
    }
    
    const guesses = JSON.parse(storedValue);
    // Validate that it's an array
    if (Array.isArray(guesses)) {
      return guesses;
    }
    return null;
  } catch (err) {
    // If parsing fails, delete the corrupted storage
    try {
      const storage = getAsyncStorage();
      if (isReactNative() && storage) {
        await storage.removeItem(storageKey);
      } else if (typeof document !== 'undefined') {
        deleteCookie(storageKey);
      }
    } catch (deleteErr) {
      // Ignore delete errors
    }
    return null;
  }
}

const HINTS_STORAGE_PREFIX = 'semantle-hints-';

/**
 * Save hints used count for a day and language.
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {number} hintsUsed - Number of hints used
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 */
export async function saveHintsUsed(dayKey, hintsUsed, lang = 'en') {
  const storageKey = `${HINTS_STORAGE_PREFIX}${lang}-${dayKey}`;
  const value = String(Math.max(0, Math.floor(Number(hintsUsed) || 0)));
  try {
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      await storage.setItem(storageKey, value);
    } else if (typeof document !== 'undefined') {
      if (!isStorageConsentGranted()) return;
      setCookie(storageKey, value, 2);
      try {
        localStorage.setItem(storageKey, value);
      } catch (e) {}
    }
  } catch (err) {}
}

/**
 * Load hints used count for a day and language.
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 * @returns {Promise<number>} Number of hints used (0+)
 */
export async function loadHintsUsed(dayKey, lang = 'en') {
  const storageKey = `${HINTS_STORAGE_PREFIX}${lang}-${dayKey}`;
  try {
    let storedValue = null;
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      storedValue = await storage.getItem(storageKey);
    } else if (typeof document !== 'undefined') {
      storedValue = getCookie(storageKey);
      if (!storedValue) {
        try {
          storedValue = localStorage.getItem(storageKey);
        } catch (e) {}
      }
    }
    if (storedValue == null || storedValue === '') return 0;
    const n = parseInt(storedValue, 10);
    return Number.isNaN(n) ? 0 : Math.max(0, n);
  } catch (err) {
    return 0;
  }
}

const LAST_HINT_AT_KEY = 'semantle-lasthint-';

/**
 * Save guess count when last hint was used (for "3 guesses before next hint" rule).
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {number} count - Number of guesses (including the hint) when hint was used
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 */
export async function saveLastHintAtGuessCount(dayKey, count, lang = 'en') {
  const storageKey = `${LAST_HINT_AT_KEY}${lang}-${dayKey}`;
  const value = String(Math.max(-1, Math.floor(Number(count) || 0)));
  try {
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      await storage.setItem(storageKey, value);
    } else if (typeof document !== 'undefined') {
      if (!isStorageConsentGranted()) return;
      setCookie(storageKey, value, 2);
      try {
        localStorage.setItem(storageKey, value);
      } catch (e) {}
    }
  } catch (err) {}
}

/**
 * Load guess count when last hint was used (-1 if none yet).
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 * @returns {Promise<number>}
 */
export async function loadLastHintAtGuessCount(dayKey, lang = 'en') {
  const storageKey = `${LAST_HINT_AT_KEY}${lang}-${dayKey}`;
  try {
    let storedValue = null;
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      storedValue = await storage.getItem(storageKey);
    } else if (typeof document !== 'undefined') {
      storedValue = getCookie(storageKey);
      if (!storedValue) {
        try {
          storedValue = localStorage.getItem(storageKey);
        } catch (e) {}
      }
    }
    if (storedValue == null || storedValue === '') return -1;
    const n = parseInt(storedValue, 10);
    return Number.isNaN(n) ? -1 : n;
  } catch (err) {
    return -1;
  }
}

/**
 * Save winning word to day-specific and language-specific storage
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {string} winningWord - The word that was correctly guessed
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 */
export async function saveWinningWord(dayKey, winningWord, lang = 'en') {
  const storageKey = `semantle-winner-${lang}-${dayKey}`;
  try {
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      // Use AsyncStorage for React Native
      await storage.setItem(storageKey, winningWord);
    } else if (typeof document !== 'undefined') {
      if (!isStorageConsentGranted()) return;
      // Use cookies for browser (with localStorage fallback)
      // Store for 2 days to handle edge cases around midnight
      setCookie(storageKey, winningWord, 2);
      
      // Also save to localStorage as backup (cookies can be unreliable)
      try {
        localStorage.setItem(storageKey, winningWord);
      } catch (e) {}
    }
  } catch (err) {}
}

/**
 * Load winning word from day-specific and language-specific storage
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
 * @returns {Promise<string|null>} Winning word or null if not found
 */
export async function loadWinningWord(dayKey, lang = 'en') {
  const storageKey = `semantle-winner-${lang}-${dayKey}`;
  try {
    let storedValue = null;
    
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      // Use AsyncStorage for React Native
      storedValue = await storage.getItem(storageKey);
    } else if (typeof document !== 'undefined') {
      // Use cookies for browser (with localStorage fallback)
      storedValue = getCookie(storageKey);
      
      // If cookie not found, try localStorage as fallback
      if (!storedValue) {
        try {
          const fromLocalStorage = localStorage.getItem(storageKey);
          if (fromLocalStorage) {
            storedValue = fromLocalStorage;
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    }
    
    return storedValue || null;
  } catch (err) {
    return null;
  }
}

/**
 * Save yesterday mode preference
 * @param {boolean} isYesterdayMode - Whether user is in yesterday mode
 */
export async function saveYesterdayMode(isYesterdayMode) {
  const storageKey = 'semantle-yesterday-mode';
  try {
    const data = JSON.stringify(isYesterdayMode);
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      await storage.setItem(storageKey, data);
    } else if (typeof document !== 'undefined') {
      if (!isStorageConsentGranted()) return;
      // Store for 2 days
      setCookie(storageKey, data, 2);
    }
  } catch (err) {}
}

/**
 * Load yesterday mode preference
 * @returns {Promise<boolean>} Whether user was in yesterday mode
 */
export async function loadYesterdayMode() {
  const storageKey = 'semantle-yesterday-mode';
  try {
    let storedValue = null;
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      storedValue = await storage.getItem(storageKey);
    } else if (typeof document !== 'undefined') {
      storedValue = getCookie(storageKey);
    }
    if (storedValue) {
      return JSON.parse(storedValue) === true;
    }
    return false;
  } catch (err) {
    return false;
  }
}

/**
 * Clean up old guess storage (keep only the current day and yesterday for all languages)
 * This helps prevent storage from growing too large
 * @param {string} currentDayKey - Current day key in format YYYY-MM-DD
 */
export async function cleanupOldGuessCookies(currentDayKey) {
  try {
    const yesterdayKey = getDayKeyBefore(currentDayKey, 1);
    const guessStoragePrefix = 'semantle-guesses-';

    const winnerStoragePrefix = 'semantle-winner-';
    const hintsStoragePrefix = HINTS_STORAGE_PREFIX;
    const lastHintPrefix = LAST_HINT_AT_KEY;
    
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      // Use AsyncStorage for React Native
      try {
        const allKeys = await storage.getAllKeys();
        const keysToDelete = allKeys
          .filter(key => key.startsWith(guessStoragePrefix) || key.startsWith(winnerStoragePrefix) || key.startsWith(hintsStoragePrefix) || key.startsWith(lastHintPrefix))
          .filter(key => {
            const prefix = key.startsWith(guessStoragePrefix) ? guessStoragePrefix : key.startsWith(winnerStoragePrefix) ? winnerStoragePrefix : key.startsWith(hintsStoragePrefix) ? hintsStoragePrefix : lastHintPrefix;
            // Format: semantle-guesses-{lang}-{YYYY-MM-DD} or semantle-winner-{lang}-{YYYY-MM-DD}
            const withoutPrefix = key.replace(prefix, '');
            const parts = withoutPrefix.split('-');
            // parts e.g. ['en', '2025', '02', '21'] -> dayKey = '2025-02-21'
            if (parts.length >= 4) {
              const dayKey = parts.slice(1).join('-');
              return dayKey !== currentDayKey && dayKey !== yesterdayKey;
            }
            // Fallback for old format (without language): key is just dayKey
            if (parts.length >= 1) {
              return withoutPrefix !== currentDayKey && withoutPrefix !== yesterdayKey;
            }
            return true;
          });
        
        if (keysToDelete.length > 0) {
          await storage.multiRemove(keysToDelete);
        }
      } catch (err) {}
    } else if (typeof document !== 'undefined') {
      // Use cookies for browser
      const cookies = document.cookie.split(';');
      
      cookies.forEach(cookie => {
        const trimmed = cookie.trim();
        if (trimmed.startsWith(guessStoragePrefix) || trimmed.startsWith(winnerStoragePrefix) || trimmed.startsWith(hintsStoragePrefix) || trimmed.startsWith(lastHintPrefix)) {
          const cookieName = trimmed.split('=')[0];
          const prefix = cookieName.startsWith(guessStoragePrefix) ? guessStoragePrefix : cookieName.startsWith(winnerStoragePrefix) ? winnerStoragePrefix : cookieName.startsWith(hintsStoragePrefix) ? hintsStoragePrefix : lastHintPrefix;
          // Format: semantle-guesses-{lang}-{YYYY-MM-DD} or semantle-winner-{lang}-{YYYY-MM-DD}
          const withoutPrefix = cookieName.replace(prefix, '');
          const parts = withoutPrefix.split('-');
          let dayKey;
          if (parts.length >= 4) {
            dayKey = parts.slice(1).join('-'); // e.g. ['en','2025','02','21'] -> '2025-02-21'
          } else {
            // Fallback for old format (without language)
            dayKey = withoutPrefix;
          }
          
          // Delete if it's not today or yesterday
          if (dayKey !== currentDayKey && dayKey !== yesterdayKey) {
            deleteCookie(cookieName);
          }
        }
      });
    }
  } catch (err) {}
}

/**
 * Clear all Semantle-related storage (for testing/debugging)
 * Removes all guesses, winning words, and preferences
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export async function clearAllStorage() {
  try {
    const guessStoragePrefix = 'semantle-guesses-';
    const winnerStoragePrefix = 'semantle-winner-';
    const hintsStoragePrefix = HINTS_STORAGE_PREFIX;
    const lastHintPrefix = LAST_HINT_AT_KEY;
    const yesterdayModeKey = 'semantle-yesterday-mode';
    
    const storage = getAsyncStorage();
    if (isReactNative() && storage) {
      // Use AsyncStorage for React Native
      try {
        const allKeys = await storage.getAllKeys();
        const keysToDelete = allKeys.filter(key => 
          key.startsWith(guessStoragePrefix) || 
          key.startsWith(winnerStoragePrefix) || 
          key.startsWith(hintsStoragePrefix) ||
          key.startsWith(lastHintPrefix) ||
          key === yesterdayModeKey
        );
        
        if (keysToDelete.length > 0) {
          await storage.multiRemove(keysToDelete);
        }
        return true;
      } catch (err) {
        return false;
      }
    } else if (typeof document !== 'undefined') {
      // Use cookies for browser
      const cookies = document.cookie.split(';');
      
      cookies.forEach(cookie => {
        const trimmed = cookie.trim();
        const cookieName = trimmed.split('=')[0];
        
        if (
          cookieName.startsWith(guessStoragePrefix) || 
          cookieName.startsWith(winnerStoragePrefix) || 
          cookieName.startsWith(hintsStoragePrefix) ||
          cookieName.startsWith(lastHintPrefix) ||
          cookieName === yesterdayModeKey
        ) {
          deleteCookie(cookieName);
        }
      });
      
      return true;
    }
    return false;
  } catch (err) {
    return false;
  }
}
